import { useState } from 'react';
import { Loader2, Target, CheckCircle2, Circle, XCircle, Pause, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { usePDIs } from '../hooks/usePDIs';
import { getQuarterName } from '../components/QuarterSelector';
import { CreatePDIForm } from '../components/CreatePDIForm';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Permission, hasPermission } from '@/shared/types/permissions';

/**
 * Página de PDI (Plano de Desenvolvimento Individual)
 */
export function PDIPage() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedStatus, setSelectedStatus] = useState<'ACTIVE' | 'CLOSED' | undefined>('ACTIVE');
  const [showForm, setShowForm] = useState(false);

  const { user } = useAuth();
  const { data, isLoading } = usePDIs({
    year: selectedYear,
    status: selectedStatus,
  });

  // Apenas gestores podem criar PDI
  const canCreatePDI = hasPermission(
    user?.role || '',
    user?.permissions,
    Permission.FEEDBACK_PDI_MANAGEMENT
  );

  // Se está mostrando o formulário, renderiza apenas ele
  if (showForm) {
    return (
      <CreatePDIForm
        onCancel={() => setShowForm(false)}
        onSuccess={() => setShowForm(false)}
      />
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'IN_PROGRESS':
        return <Circle className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      case 'ABORTED':
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
      default:
        return <Pause className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'default' | 'outline' | 'error'> = {
      COMPLETED: 'success',
      IN_PROGRESS: 'default',
      NOT_STARTED: 'outline',
      ABORTED: 'error',
    };

    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      IN_PROGRESS: 'Em Progresso',
      NOT_STARTED: 'Não Iniciado',
      ABORTED: 'Abortado',
    };

    return <Badge variant={variants[status] || 'outline'}>{labels[status] || status}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">PDI - Plano de Desenvolvimento Individual</h1>
          <p className="text-muted-foreground">Acompanhe seus objetivos e desenvolvimento trimestral</p>
        </div>

        {canCreatePDI && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Criar PDI
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4">
        <Select
          label="Ano"
          value={selectedYear.toString()}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          options={[2024, 2025, 2026].map((year) => ({
            value: year.toString(),
            label: year.toString(),
          }))}
        />

        <Select
          label="Status"
          value={selectedStatus || 'ALL'}
          onChange={(e) =>
            setSelectedStatus(e.target.value === 'ALL' ? undefined : (e.target.value as 'ACTIVE' | 'CLOSED'))
          }
          options={[
            { value: 'ALL', label: 'Todos' },
            { value: 'ACTIVE', label: 'Ativos' },
            { value: 'CLOSED', label: 'Encerrados' },
          ]}
        />
      </div>

      {/* Lista de PDIs */}
      <div className="space-y-4">
        {data && data.pdis.length > 0 ? (
          data.pdis.map((pdi) => (
            <Card key={pdi.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      {pdi.employee.user.name} - {getQuarterName(pdi.quarter)} {pdi.year}
                    </CardTitle>
                  </div>

                  <Badge variant={pdi.status === 'ACTIVE' ? 'default' : 'outline'}>
                    {pdi.status === 'ACTIVE' ? 'Ativo' : 'Encerrado'}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  {/* Objetivos */}
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Objetivos ({pdi.objectives.length})</h4>
                    <div className="space-y-3">
                      {pdi.objectives.map((objective, index) => (
                        <div key={objective.id} className="border-l-4 border-primary pl-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {getStatusIcon(objective.status)}
                                <h5 className="font-medium">
                                  {index + 1}. {objective.title}
                                </h5>
                                {getStatusBadge(objective.status)}
                              </div>

                              <p className="text-sm text-foreground mb-2">
                                <strong>Ação:</strong> {objective.action}
                              </p>

                              <p className="text-sm text-foreground mb-2">
                                <strong>Indicador de Sucesso:</strong> {objective.successIndicator}
                              </p>

                              {objective.supportNeeded && (
                                <p className="text-sm text-blue-700 dark:text-blue-400">
                                  <strong>Apoio Necessário:</strong> {objective.supportNeeded}
                                </p>
                              )}

                              <p className="text-xs text-muted-foreground mt-2">
                                <strong>Prazo:</strong>{' '}
                                {new Date(objective.deadline).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Avaliação Final (se encerrado) */}
                  {pdi.status === 'CLOSED' && pdi.finalScore && (
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-semibold mb-3">Avaliação Final</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">
                            Autoavaliação do Colaborador:
                          </p>
                          <p className="text-sm text-muted-foreground">{pdi.employeeSelfAssessment}</p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">Avaliação do Gestor:</p>
                          <p className="text-sm text-muted-foreground">{pdi.managerAssessment}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Nota Global:</span>
                        <Badge variant="success" className="text-lg">
                          {pdi.finalScore}/5
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              Nenhum PDI encontrado para este período.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
